import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import centerDiagramInScrollContainer from "@/components/diagrams/shared/centerDiagramInScrollContainer";
import useBreakPointBasedProp from "@/hooks/useBreakPointBasedProp";

import Box from "@/components/Box";
import CloseButton from "@/components/CloseButton";
import Loading from "@/components/Loading";
import { MODAL_Z_INDEX } from "@/core/constants";
import { theme } from "@/core/theme";
import DIAGRAM_KEY_CONTROLS_IDS from "@/components/diagrams/diagramKeyControlsIds";
import DiagramZoomControls, { zoomIn, zoomOut } from "@/components/diagrams/shared/DiagramZoomControls";
import KeyToggleButton from "@/components/diagrams/shared/KeyToggleButton";
import SnpFamilyModal from "@/components/diagrams/shared/SnpFamilyModal";
import CardiometabolicAppetiteControlDiagramKey from "@/components/diagrams/CardiometabolicAppetiteControlDiagramKey";
import CardiometabolicCircadianRhythmDiagramKey from "@/components/diagrams/CardiometabolicCircadianRhythmDiagramKey";
import CardiometabolicFatMetabolismDiagramKey from "@/components/diagrams/CardiometabolicFatMetabolismDiagramKey";
import CardiometabolicInflammationDiagramKey from "@/components/diagrams/CardiometabolicInflammationDiagramKey";
import CardiometabolicNitricOxideDiagramKey from "@/components/diagrams/CardiometabolicNitricOxideDiagramKey";
import CardiometabolicOmega3DiagramKey from "@/components/diagrams/CardiometabolicOmega3DiagramKey";
import CardiometabolicOverviewDiagramKey from "@/components/diagrams/CardiometabolicOverviewDiagramKey";
import CardiometabolicSodiumPotassiumDiagramKey from "@/components/diagrams/CardiometabolicSodiumPotassiumDiagramKey";
import CardiometabolicSugarMetabolismDiagramKey from "@/components/diagrams/CardiometabolicSugarMetabolismDiagramKey";
import EndocrineAndrogensDiagramKey from "@/components/diagrams/EndocrineAndrogensDiagramKey";
import EndocrineOestrogenDiagramKey from "@/components/diagrams/EndocrineOestrogenDiagramKey";
import EndocrineOverviewDiagramKey from "@/components/diagrams/EndocrineOverviewDiagramKey";
import EndocrineProgesteroneDiagramKey from "@/components/diagrams/EndocrineProgesteroneDiagramKey";
import EndocrineStressCortisolCortisoneDiagramKey from "@/components/diagrams/EndocrineStressCortisolCortisoneDiagramKey";
import EndocrineThyroidDiagramKey from "@/components/diagrams/EndocrineThyroidDiagramKey";
import MethylationBh4CycleDiagramKey from "@/components/diagrams/MethylationBh4CycleDiagramKey";
import MethylationDetoxificationIntermediaryDiagramKey from "@/components/diagrams/MethylationDetoxificationIntermediaryDiagramKey";
import MethylationDetoxificationOverviewDiagramKey from "@/components/diagrams/MethylationDetoxificationOverviewDiagramKey";
import MethylationDetoxificationPhase1DiagramKey from "@/components/diagrams/MethylationDetoxificationPhase1DiagramKey";
import MethylationDetoxificationPhase2DiagramKey from "@/components/diagrams/MethylationDetoxificationPhase2DiagramKey";
import MethylationFolateDiagramKey from "@/components/diagrams/MethylationFolateDiagramKey";
import MethylationMethionineDiagramKey from "@/components/diagrams/MethylationMethionineDiagramKey";
import MethylationMethylationDiagramKey from "@/components/diagrams/MethylationMethylationDiagramKey";
import MethylationNitricOxideDiagramKey from "@/components/diagrams/MethylationNitricOxideDiagramKey";
import MethylationTranssulphurationDiagramKey from "@/components/diagrams/MethylationTranssulphurationDiagramKey";
import NutritionFolateDiagramKey from "@/components/diagrams/NutritionFolateDiagramKey";
import NutritionIodineDiagramKey from "@/components/diagrams/NutritionIodineDiagramKey";
import NutritionIronOverloadDiagramKey from "@/components/diagrams/NutritionIronOverloadDiagramKey";
import NutritionOmega3DiagramKey from "@/components/diagrams/NutritionOmega3DiagramKey";
import NutritionSodiumPotassiumDiagramKey from "@/components/diagrams/NutritionSodiumPotassiumDiagramKey";
import NutritionVitaminADiagramKey from "@/components/diagrams/NutritionVitaminADiagramKey";
import NutritionVitaminB12DiagramKey from "@/components/diagrams/NutritionVitaminB12DiagramKey";
import NutritionVitaminDDiagramKey from "@/components/diagrams/NutritionVitaminDDiagramKey";
import NutritionZincDiagramKey from "@/components/diagrams/NutritionZincDiagramKey";
import Modal from "@/tpo/Modal";
import Stack from "@/tpo/Stack";
import { buildDiagramGeneColors } from "@/utils/getSnpColor";

const DIAGRAM_COMPONENTS = {
  CardiometabolicAppetiteControlDiagramKey,
  CardiometabolicCircadianRhythmDiagramKey,
  CardiometabolicFatMetabolismDiagramKey,
  CardiometabolicInflammationDiagramKey,
  CardiometabolicNitricOxideDiagramKey,
  CardiometabolicOmega3DiagramKey,
  CardiometabolicSodiumPotassiumDiagramKey,
  CardiometabolicOverviewDiagramKey,
  CardiometabolicSugarMetabolismDiagramKey,
  EndocrineAndrogensDiagramKey,
  EndocrineOestrogenDiagramKey,
  EndocrineOverviewDiagramKey,
  EndocrineProgesteroneDiagramKey,
  EndocrineStressCortisolCortisoneDiagramKey,
  EndocrineThyroidDiagramKey,
  MethylationBh4CycleDiagramKey,
  MethylationDetoxificationIntermediaryDiagramKey,
  MethylationDetoxificationOverviewDiagramKey,
  MethylationDetoxificationPhase1DiagramKey,
  MethylationDetoxificationPhase2DiagramKey,
  MethylationFolateDiagramKey,
  MethylationMethionineDiagramKey,
  MethylationMethylationDiagramKey,
  MethylationNitricOxideDiagramKey,
  MethylationTranssulphurationDiagramKey,
  NutritionFolateDiagramKey,
  NutritionIodineDiagramKey,
  NutritionIronOverloadDiagramKey,
  NutritionOmega3DiagramKey,
  NutritionSodiumPotassiumDiagramKey,
  NutritionVitaminADiagramKey,
  NutritionVitaminB12DiagramKey,
  NutritionVitaminDDiagramKey,
  NutritionZincDiagramKey
};

const toTitleCase = value =>
  value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .trim();

const DIAGRAM_MODAL_INSET = [20, 20, 40];
const DIAGRAM_MODAL_CLOSE_SIZE = [40, 40, 48];

export default function DiagramModal({
  show,
  close,
  diagramName,
  title,
  snpsForDiagram,
  expectedSnps,
  loading = false
}) {
  const DiagramComponent = diagramName ? DIAGRAM_COMPONENTS[diagramName] : null;
  const heading = title || (diagramName ? toTitleCase(diagramName) : "Diagram");
  const closeRef = useRef(close);
  const historyPushedRef = useRef(false);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);
  const [showKey, setShowKey] = useState(false);
  const [chromeHeight, setChromeHeight] = useState(0);
  const scrollContainerRef = useRef(null);
  const chromeRef = useRef(null);
  const toolbarRef = useRef(null);
  const diagramRef = useRef(null);
  const modalInset = useBreakPointBasedProp(DIAGRAM_MODAL_INSET);
  const keyControlsId = diagramName ? DIAGRAM_KEY_CONTROLS_IDS[diagramName] : null;

  closeRef.current = close;

  const handleGeneClick = useCallback(
    familyName => {
      if (!snpsForDiagram?.[familyName]) return;
      setSelectedFamily(familyName);
    },
    [snpsForDiagram]
  );

  const removeHistoryEntry = useCallback(() => {
    if (!historyPushedRef.current) return;
    historyPushedRef.current = false;
    if (window.history.state?.diagramModal) {
      window.history.back();
    }
  }, []);

  useEffect(() => {
    setZoomScale(1);
    setShowKey(false);
  }, [diagramName, show]);

  const centerDiagramInView = useCallback(() => {
    centerDiagramInScrollContainer(
      scrollContainerRef.current,
      diagramRef.current,
      toolbarRef.current
    );
  }, []);

  useLayoutEffect(() => {
    if (!show || !DiagramComponent || zoomScale === 1) return;

    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        centerDiagramInView();
      });
    });

    return () => cancelAnimationFrame(frame);
  }, [zoomScale, showKey, show, diagramName, DiagramComponent, centerDiagramInView]);

  useLayoutEffect(() => {
    if (!show) {
      setChromeHeight(0);
      return;
    }

    const chromeEl = chromeRef.current;
    if (!chromeEl) return;

    setChromeHeight(modalInset + chromeEl.offsetHeight);
  }, [show, DiagramComponent, modalInset, keyControlsId, showKey, diagramName, heading]);

  // Close modal on browser back button
  useEffect(() => {
    if (!show) {
      setSelectedFamily(null);
      removeHistoryEntry();
      return;
    }

    window.history.pushState({ diagramModal: true }, "");
    historyPushedRef.current = true;

    const handlePopState = () => {
      historyPushedRef.current = false;
      closeRef.current();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      removeHistoryEntry();
    };
  }, [show, removeHistoryEntry]);

  const geneColors = useMemo(
    () => buildDiagramGeneColors(snpsForDiagram, expectedSnps),
    [snpsForDiagram, expectedSnps]
  );

  return (
    <Modal
      mode={["fullScreen", "fullScreen", "centered"]}
      show={show}
      close={close}
      maxWidth={1728}
      bg="white"
      overlayBg="black"
      overlayOpacity={0.5}
      borderRadius={[0, 0, 20]}
      innerProps={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        overflowY: "hidden"
      }}
      outerProps={{
        overflow: "hidden"
      }}
    >
      <Box
        position="absolute"
        top={DIAGRAM_MODAL_INSET}
        right={DIAGRAM_MODAL_INSET}
        zIndex={MODAL_Z_INDEX + 1}
      >
        <CloseButton
          height={DIAGRAM_MODAL_CLOSE_SIZE}
          width={DIAGRAM_MODAL_CLOSE_SIZE}
          color={theme.colors.dark}
          onClick={close}
          bg="white"
          data-component-name="ModalCloseButton"
        />
      </Box>
      <Box
        ref={chromeRef}
        position="absolute"
        top={DIAGRAM_MODAL_INSET}
        left={0}
        right={0}
        zIndex={MODAL_Z_INDEX}
      >
        <Box display="flex" alignItems="flex-start" px={DIAGRAM_MODAL_INSET}>
          <Box fontFamily="gilroyBold" fontSize={[32, 32, 44]} flex={1} minWidth={0}>
            {heading}
          </Box>
          <Box width={DIAGRAM_MODAL_CLOSE_SIZE} flexShrink={0} aria-hidden />
        </Box>
        {DiagramComponent ? (
          <Box ref={toolbarRef} ml={[16, 16, 40]} mt={[12, 12, 20]} lineHeight="normal">
            <Box display="inline-flex" alignItems="center" gap={12}>
              {keyControlsId ? (
                <KeyToggleButton
                  showKey={showKey}
                  controlsId={keyControlsId}
                  onChange={setShowKey}
                />
              ) : null}
              <DiagramZoomControls
                scale={zoomScale}
                onZoomIn={() => setZoomScale(current => zoomIn(current))}
                onZoomOut={() => setZoomScale(current => zoomOut(current))}
              />
            </Box>
          </Box>
        ) : null}
      </Box>
      {selectedFamily && snpsForDiagram?.[selectedFamily] ? (
        <SnpFamilyModal
          familyName={selectedFamily}
          familyData={snpsForDiagram[selectedFamily]}
          onClose={() => setSelectedFamily(null)}
        />
      ) : null}
      <Box
        ref={scrollContainerRef}
        flex={1}
        minHeight={0}
        overflow="auto"
        display="flex"
        flexDirection="column"
      >
        <Box height={chromeHeight} flexShrink={0} aria-hidden />
        {loading ? (
          <Box flex={1} display="flex" alignItems="center" justifyContent="center">
            <Loading />
          </Box>
        ) : (
          <Stack px={[16, 16, 40]} gap={[12, 12, 20]} pb={[20, 20, 40]}>
            {DiagramComponent ? (
              <DiagramComponent
                geneColors={geneColors}
                expectedSnps={expectedSnps}
                onGeneClick={handleGeneClick}
                zoomScale={zoomScale}
                hideKeyToggle
                showKey={showKey}
                onShowKeyChange={setShowKey}
                zoomContentRef={diagramRef}
                style={{ height: "auto" }}
              />
            ) : (
              <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} lineHeight="24px">
                No diagram configured for this item.
              </Box>
            )}
          </Stack>
        )}
      </Box>
    </Modal>
  );
}
